import { AUDIENCES, createAnnouncementSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import type { Announcement, AudienceKey, Message, Person, Preview, SchoolClass } from './shared';

const blank = { title: '', body: '', audience: 'parents' as AudienceKey, email: false, sms: false };

export function AnnouncementsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const sending = can('comms.send', schoolId);
  const [form, setForm] = useState(blank);
  const [classId, setClassId] = useState('');
  const [search, setSearch] = useState('');
  const [student, setStudent] = useState<Person | null>(null);
  const [scheduleFor, setScheduleFor] = useState('');
  const [when, setWhen] = useState('');
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const list = useQuery({
    queryKey: ['comms', schoolId, 'announcements', page],
    queryFn: () =>
      apiList<Announcement>(`/schools/${schoolId}/comms/announcements`, {
        query: { page, pageSize: 20 },
      }),
  });
  const classes = useQuery({
    queryKey: ['comms', schoolId, 'classes'],
    queryFn: () => api<SchoolClass[]>(`/schools/${schoolId}/classes`),
    enabled: sending && form.audience === 'class',
  });
  const matches = useQuery({
    queryKey: ['comms', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: sending && form.audience === 'student' && search.trim().length >= 2 && !student,
  });
  const target = {
    audience: form.audience,
    ...(form.audience === 'class' && classId ? { classId } : {}),
    ...(form.audience === 'student' && student ? { studentId: student.id } : {}),
  };
  const ready =
    form.audience === 'class'
      ? Boolean(classId)
      : form.audience === 'student'
        ? Boolean(student)
        : true;
  const preview = useQuery({
    queryKey: ['comms', schoolId, 'audience', target],
    queryFn: () =>
      api<Preview>(`/schools/${schoolId}/comms/announcements/audience`, { query: target }),
    enabled: sending && ready,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['comms', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/comms/announcements`, { method: 'POST', body }),
    onSuccess: () => {
      setForm(blank);
      setClassId('');
      setStudent(null);
      setSearch('');
      setMessage({ kind: 'ok', text: t('cm.saved') });
      refresh();
    },
    onError: fail,
  });
  const act = useMutation({
    mutationFn: (args: {
      id: string;
      what: 'publish' | 'cancel' | 'delete';
      publishAt?: string;
    }) =>
      args.what === 'delete'
        ? api(`/schools/${schoolId}/comms/announcements/${args.id}`, { method: 'DELETE' })
        : api(`/schools/${schoolId}/comms/announcements/${args.id}/${args.what}`, {
            method: 'POST',
            body:
              args.what === 'publish'
                ? args.publishAt
                  ? { publishAt: args.publishAt }
                  : {}
                : undefined,
          }),
    onSuccess: (_data, args) => {
      setScheduleFor('');
      setWhen('');
      setMessage({
        kind: 'ok',
        text: t(
          args.what === 'publish'
            ? args.publishAt
              ? 'cm.scheduled'
              : 'cm.published'
            : args.what === 'cancel'
              ? 'cm.cancelled'
              : 'cm.deleted',
        ),
      });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('cm.tab.announcements')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('cm.none')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {list.data?.data.map((a) => (
          <li key={a.id} className="space-y-2 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span>
                <span className="font-medium">{a.title}</span>
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">
                  {t(`cm.status.${a.status}` as MessageKey)}
                </span>
                <br />
                <span className="text-slate-600">
                  {t(`cm.audience.${a.audience}` as MessageKey)}
                  {a.className ? `: ${a.className}` : ''}
                  {a.studentName ? `: ${a.studentName}` : ''}
                  {a.channelEmail ? ` · ${t('cm.email')}` : ''}
                  {a.channelSms ? ` · ${t('cm.sms')}` : ''}
                  {a.publishAt && a.status === 'scheduled'
                    ? ` · ${t('cm.sendsAt', { time: new Date(a.publishAt).toLocaleString() })}`
                    : ''}
                </span>
              </span>
              {sending && (a.status === 'draft' || a.status === 'scheduled') && (
                <span className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={primaryButton}
                    aria-label={`${t('cm.publishNow')}: ${a.title}`}
                    disabled={act.isPending}
                    onClick={() =>
                      window.confirm(t('cm.confirmSend', { title: a.title })) &&
                      act.mutate({ id: a.id, what: 'publish' })
                    }
                  >
                    {t('cm.publishNow')}
                  </button>
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('cm.schedule')}: ${a.title}`}
                    onClick={() => setScheduleFor(scheduleFor === a.id ? '' : a.id)}
                  >
                    {t('cm.schedule')}
                  </button>
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('cm.cancel')}: ${a.title}`}
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: a.id, what: 'cancel' })}
                  >
                    {t('cm.cancel')}
                  </button>
                </span>
              )}
              {sending && (a.status === 'draft' || a.status === 'cancelled') && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('cm.delete')}: ${a.title}`}
                  disabled={act.isPending}
                  onClick={() =>
                    window.confirm(t('common.confirmDelete', { name: a.title })) &&
                    act.mutate({ id: a.id, what: 'delete' })
                  }
                >
                  {t('cm.delete')}
                </button>
              )}
            </div>
            {scheduleFor === a.id && (
              <form
                noValidate
                aria-label={t('cm.scheduleForm')}
                className="flex flex-wrap items-end gap-3 rounded bg-slate-50 p-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const at = new Date(when);
                  if (Number.isNaN(at.getTime())) return;
                  act.mutate({ id: a.id, what: 'publish', publishAt: at.toISOString() });
                }}
              >
                <TextField
                  type="datetime-local"
                  label={t('cm.sendAt')}
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                />
                <button type="submit" className={primaryButton} disabled={!when || act.isPending}>
                  {t('cm.scheduleNow')}
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {list.data && list.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
      )}

      {sending && (
        <form
          noValidate
          aria-label={t('cm.compose')}
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createAnnouncementSchema.safeParse({
              title: form.title,
              body: form.body,
              ...target,
              channelEmail: form.email,
              channelSms: form.sms,
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            create.mutate(parsed.data);
          }}
        >
          <TextField
            label={t('cm.titleField')}
            value={form.title}
            error={errors.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div>
            <label htmlFor="cm-body" className="block text-sm font-medium">
              {t('cm.body')}
            </label>
            <textarea
              id="cm-body"
              rows={5}
              className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm"
              value={form.body}
              aria-invalid={errors.body ? true : undefined}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
            {errors.body && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {errors.body}
              </p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="cm-audience" className="block text-sm font-medium">
                {t('cm.audience')}
              </label>
              <select
                id="cm-audience"
                className={selectClass}
                value={form.audience}
                onChange={(e) => {
                  setForm({ ...form, audience: e.target.value as AudienceKey });
                  setClassId('');
                  setStudent(null);
                  setSearch('');
                }}
              >
                {AUDIENCES.map((x) => (
                  <option key={x} value={x}>
                    {t(`cm.audience.${x}` as MessageKey)}
                  </option>
                ))}
              </select>
              {errors.audience && (
                <p role="alert" className="mt-1 text-xs text-red-700">
                  {errors.audience}
                </p>
              )}
            </div>
            {form.audience === 'class' && (
              <div>
                <label htmlFor="cm-class" className="block text-sm font-medium">
                  {t('cm.class')}
                </label>
                <select
                  id="cm-class"
                  className={selectClass}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                >
                  <option value="">{t('cm.choose')}</option>
                  {classes.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {form.audience === 'student' && (
              <div className="relative">
                <TextField
                  label={t('cm.student')}
                  value={student ? `${student.firstName} ${student.lastName}` : search}
                  onChange={(e) => {
                    setStudent(null);
                    setSearch(e.target.value);
                  }}
                />
                {!student && matches.data && matches.data.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                    {matches.data.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="w-full p-2 text-left hover:bg-slate-50"
                          onClick={() => setStudent(p)}
                        >
                          {p.firstName} {p.lastName} · {p.admissionNumber}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <fieldset className="text-sm">
              <legend className="font-medium">{t('cm.channels')}</legend>
              <label className="mt-1 flex items-center gap-2">
                <input type="checkbox" checked disabled /> {t('cm.inApp')}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.checked })}
                />{' '}
                {t('cm.email')}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.sms}
                  onChange={(e) => setForm({ ...form, sms: e.target.checked })}
                />{' '}
                {t('cm.sms')}
              </label>
            </fieldset>
          </div>
          {preview.data && (
            <p role="status" className="rounded bg-slate-50 p-3 text-sm">
              {t('cm.reach', {
                people: preview.data.people,
                inApp: preview.data.inApp,
                email: preview.data.email,
                sms: preview.data.sms,
              })}
              {form.email && !preview.data.emailEnabled ? ` ${t('cm.emailOff')}` : ''}
              {form.sms && !preview.data.smsEnabled ? ` ${t('cm.smsOff')}` : ''}
            </p>
          )}
          <button type="submit" className={primaryButton} disabled={create.isPending}>
            {t('cm.saveDraft')}
          </button>
        </form>
      )}
    </section>
  );
}
